import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchMentorsBatch, type MentorBatchItem } from '../api/personas';
import { stationCompanions } from '../datas/stationCompanions';
import {
  getAllCompanions,
  getFavouriteCompanionsPaginated,
  getFeaturedCompanions,
} from '../services/api';
import { Companion } from '../types';

function isMentorUuid(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    id.trim(),
  );
}

function companionNeedsMediaHydration(c: Companion): boolean {
  const hasVideo = !!(c.videoLoopUrl?.trim() || c.videoIdleLoopUrl?.trim());
  const hasPoster = !!(c.previewImageUrl?.trim() || c.previewUrl?.trim());
  return !hasVideo && !hasPoster;
}

function applyBatchMentorMedia(item: Companion, m: MentorBatchItem): Companion {
  const video =
    (m.videoLoopUrl && m.videoLoopUrl.trim()) || item.videoLoopUrl || '';
  const poster =
    (m.previewUrl && m.previewUrl.trim()) ||
    (m.previewImageUrl && m.previewImageUrl.trim()) ||
    item.previewUrl ||
    item.previewImageUrl ||
    '';
  return {
    ...item,
    mentorId: String(m.mentorId || item.mentorId),
    slug: m.slug || item.slug,
    firstName: m.firstName ?? item.firstName,
    lastName: m.lastName ?? item.lastName,
    videoLoopUrl: video,
    previewUrl: poster || item.previewUrl,
    previewImageUrl: poster || item.previewImageUrl,
    jobTitle: m.jobTitle || item.jobTitle,
    headline: m.headline || item.headline,
    call_to_action: m.call_to_action ?? item.call_to_action,
  };
}

/** Favorites not in static JSON: one batched mentors call per page (chunked). */
async function hydrateFavoriteCompanionsMedia(
  items: Companion[],
): Promise<Companion[]> {
  const idsToFetch = [
    ...new Set(
      items
        .filter((c) => companionNeedsMediaHydration(c))
        .map((c) => c.mentorId?.trim())
        .filter((id): id is string => !!id && isMentorUuid(id)),
    ),
  ];
  if (!idsToFetch.length) return items;

  let byId = new Map<string, MentorBatchItem>();
  try {
    const batchItems = await fetchMentorsBatch(idsToFetch);
    byId = new Map(batchItems.map((m) => [String(m.mentorId), m]));
  } catch {
    return items;
  }

  return items.map((item) => {
    if (!companionNeedsMediaHydration(item)) return item;
    const m = byId.get(String(item.mentorId));
    if (!m) return item;
    return applyBatchMentorMedia(item, m);
  });
}

interface FavoriteCompanionsInfiniteScrollOptions {
  rootMargin?: string;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
}

interface FavoriteCompanionsInfiniteScrollState {
  items: Companion[];
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  total: number;
  loadMore: () => Promise<void>;
  reset: () => void;
  removeItem: (id: string) => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
}

export const useFavoriteCompanionsInfiniteScroll = ({
  rootMargin = '200px',
  scrollContainerRef,
  enabled = true,
}: FavoriteCompanionsInfiniteScrollOptions): FavoriteCompanionsInfiniteScrollState => {
  const [items, setItems] = useState<Companion[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [companionMap, setCompanionMap] = useState<Map<string, Companion>>(
    new Map(),
  );
  const [companionsLoaded, setCompanionsLoaded] = useState(false);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef(false);
  const offsetRef = useRef(0);
  const loadMoreRef = useRef<(() => Promise<void>) | null>(null);

  // Load all companions data once for enrichment (from mentors.json, mentors_featured.json, AND stationCompanions)
  useEffect(() => {
    if (!enabled || companionsLoaded) return;

    const loadCompanions = async () => {
      try {
        // Fetch from all sources in parallel
        const [allCompanions, featuredCompanions] = await Promise.all([
          getAllCompanions(),
          getFeaturedCompanions(),
        ]);

        const map = new Map<string, Companion>();

        // Priority order: Featured > All > Station
        // Add in REVERSE priority order so higher priority items overwrite lower ones

        // 1. Add station companions FIRST (lowest priority)
        stationCompanions.forEach((sc) => {
          const companion: Companion = {
            mentorId: sc.companion_id || sc.id,
            firstName: sc.given_name?.split(' ')[0] || sc.alias || '',
            lastName: sc.given_name?.split(' ').slice(1).join(' ') || '',
            videoLoopUrl: sc.video_url || '',
            jobTitle: sc.title || '',
            location: '',
            industry: sc.capability || '',
            company: '',
            headline: sc.title || '',
            previewUrl: sc.image_url || '',
            videoIdleLoopUrl: sc.video_url || '',
            previewImageUrl: sc.image_url || sc.full_image_url || '',
            slug: sc.slug || '',
            status: 'active',
            call_to_action: sc.placeholder || '',
          };

          // Map by companion_id (same as mentorId)
          if (sc.companion_id) {
            map.set(sc.companion_id, companion);
          }
          if (sc.id) {
            map.set(sc.id, companion);
          }
          // Map by slug for matching favorites
          if (sc.slug) {
            map.set(sc.slug, companion);
          }
        });

        // 2. Add companions from mentors.json (getAllCompanions) - overwrites station duplicates
        allCompanions.forEach((c) => {
          if (c.mentorId) {
            map.set(String(c.mentorId), c);
          }
          if (c.slug) {
            map.set(c.slug, c);
          }
        });

        // 3. Add companions from mentors_featured.json (getFeaturedCompanions) - HIGHEST priority, overwrites all duplicates
        featuredCompanions.forEach((c) => {
          if (c.mentorId) {
            map.set(String(c.mentorId), c);
          }
          if (c.slug) {
            map.set(c.slug, c);
          }
        });

        setCompanionMap(map);
        setCompanionsLoaded(true);
      } catch (err) {
        console.error('Error loading companions for enrichment:', err);
        setCompanionsLoaded(true); // Continue even if enrichment fails
      }
    };

    loadCompanions();
  }, [enabled, companionsLoaded]);

  // Reset function to clear data and restart
  const reset = useCallback(() => {
    offsetRef.current = 0;
    setItems([]);
    setHasMore(true);
    setError(null);
    setTotal(0);
    setInitialLoadDone(false);
    // Note: We don't reset companionsLoaded/companionMap as they can be reused
  }, []);

  // Remove item function - uses mentorId (companionId) for companions
  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.mentorId !== id));
    setTotal((prev) => Math.max(0, prev - 1));
  }, []);

  // Load more function
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await getFavouriteCompanionsPaginated(
        12,
        offsetRef.current,
      );

      // Enrich favorite items with full companion data from the map
      // Favorites from player / generation use the same API; if they're not in mentors JSON,
      // we still list them (otherwise total count > visible rows).
      const enrichedItems: Companion[] = (result?.items || [])
        .map((fav: { id?: string; companionId?: string; slug?: string }) => {
          const companionId =
            fav.id != null && String(fav.id) !== ''
              ? String(fav.id)
              : fav.companionId != null && String(fav.companionId) !== ''
                ? String(fav.companionId)
                : '';
          const slug = typeof fav.slug === 'string' ? fav.slug : '';

          let fullCompanion: Companion | undefined;
          if (companionId) {
            fullCompanion = companionMap.get(companionId);
          }
          if (!fullCompanion && slug) {
            fullCompanion = companionMap.get(slug);
          }

          if (fullCompanion) {
            // Do not spread `fav` — API fields can clobber mentor media on edge responses.
            return {
              ...fullCompanion,
              mentorId: String(fullCompanion.mentorId),
            } as Companion;
          }

          const mentorKey = companionId || slug;
          if (!mentorKey) {
            return null;
          }

          return {
            mentorId: mentorKey,
            slug: slug || mentorKey,
            firstName: '',
            lastName: '',
            videoLoopUrl: '',
            jobTitle: '',
            location: '',
            industry: '',
            company: '',
            headline: slug || 'Companion',
            previewUrl: '',
            previewImageUrl: '',
            videoIdleLoopUrl: '',
            status: 'active',
            call_to_action: '',
          } as Companion;
        })
        .filter((item): item is Companion => item != null);

      const hydratedItems = await hydrateFavoriteCompanionsMedia(enrichedItems);

      if (offsetRef.current === 0) {
        setItems(hydratedItems);
      } else {
        setItems((prev) => [...prev, ...hydratedItems]);
      }

      const hasNext = result?.pagination?.hasNext || false;
      // Get total from API pagination.total (as shown in API response)
      const paginationTotal = result?.pagination?.total;
      const itemsLength = result?.items?.length || 0;
      const totalCount =
        typeof paginationTotal === 'number' ? paginationTotal : itemsLength;

      setHasMore(hasNext);
      setTotal(totalCount);
      offsetRef.current += itemsLength;

      if (!initialLoadDone) {
        setInitialLoadDone(true);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to load favorite companions';
      setError(errorMessage);
      console.error('Error loading favorite companions:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [hasMore, initialLoadDone, companionMap]);

  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  // Note: No reset() on enabled change - matches useCategoryFavoritesInfiniteScroll pattern

  useEffect(() => {
    if (!enabled || !hasMore || !initialLoadDone || !sentinelRef.current)
      return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          !loadingRef.current &&
          hasMore &&
          loadMoreRef.current
        ) {
          loadMoreRef.current();
        }
      },
      {
        root: scrollContainerRef?.current || null,
        rootMargin,
        threshold: 0.1,
      },
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [enabled, hasMore, initialLoadDone, rootMargin, scrollContainerRef]);

  // Wait for companions data to be loaded before starting initial fetch
  useEffect(() => {
    if (
      !initialLoadDone &&
      enabled &&
      companionsLoaded &&
      loadMoreRef.current
    ) {
      setInitialLoadDone(true);
      loadMoreRef.current();
    }
  }, [enabled, initialLoadDone, companionsLoaded]);

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return {
    items,
    loading,
    hasMore,
    error,
    total,
    loadMore: loadMoreRef.current || (() => Promise.resolve()),
    reset,
    removeItem,
    sentinelRef,
  };
};

export default useFavoriteCompanionsInfiniteScroll;
